import { Graph, type FramePixels } from "@/libs/cv"
import {
  create as createFaceMeshGraph,
  type FaceDetection,
  type Options as FaceMeshOptions,
} from "@/libs/cv/graphs/face-mesh/face-mesh"
import {
  create as createIlluminationGraph,
  type IlluminationDetection,
} from "@/libs/cv/graphs/illumination/illumination"
import { PixelsReader } from "@/libs/cv/modules/pixels-reader/pixels-reader"
import type { ImageSource } from "@/libs/image"
import { mapValues } from "@/libs/lodash"
import { mean, round } from "@/libs/math"
import { createAggregatedProgress } from "@/libs/progress"
import type { PerformanceEntry } from "@/libs/telemetry/performance"
import { getLightingIntensityThresholds } from "../../../features/presets/face/validation"
export { defaultIllumination } from "@/libs/cv/modules/illumination/illumination"
export type { IlluminationDetection }

export interface Detections {
  face: FaceDetection | null
  illumination: IlluminationDetection | null
}

export class Detector {
  #face?: AsyncIterator<FaceDetection | null, FaceDetection | null>
  #illumination?: AsyncIterator<IlluminationDetection, IlluminationDetection>
  #pixelsReader?: PixelsReader

  #faceGraph?: Graph<[ImageSource], [FaceDetection | null]>
  #illuminationGraph?: Graph<[FramePixels, FaceDetection], [IlluminationDetection]>

  constructor(
    private _options?: Omit<FaceMeshOptions, "performance"> &
      Partial<{
        performance: PerformanceEntry
        initialState: { illumination: { intensity: number } }
        mode: "IMAGE" | "VIDEO"
        onReady: () => void
        onError: (error: Error) => void
      }>,
  ) {
    this._init().then(() => _options?.onReady?.())
  }

  async getDetections(frame: ImageSource): Promise<Detections> {
    const pixels = this._getPixels(frame)
    const face = await this._getFaceDetections(frame)

    return { face, illumination: face ? await this._getIllumination(face, pixels) : null }
  }

  protected _init = async () => {
    const perf = this._options?.performance?.mark("cv:preset-creation")

    const onProgress = this._options?.onProgress
      ? createAggregatedProgress({
          // 80% for dependencies
          // 20% for the rest
          coeffs: [0.8, 0.2],
          onProgress: this._options.onProgress,
        })
      : undefined

    try {
      await this._initDependencies({ perf, onProgress: onProgress?.bind(null, 0) })
    } catch (e) {
      this._options?.onError?.(e as Error)
      throw e
    }

    onProgress?.(1, 1)

    perf?.measure({
      detail:
        typeof navigator.connection === "undefined"
          ? undefined
          : {
              "connection.type": navigator.connection.type,
              "connection.effectiveType": navigator.connection.effectiveType,
              "connection.downlink": navigator.connection.downlink,
              "connection.saveData": navigator.connection.saveData,
            },
    })
  }

  protected async _initDependencies({
    perf,
    ...options
  }: {
    perf?: { child: () => PerformanceEntry }
    onProgress?: (progress: number) => void
  }) {
    const onProgress = options?.onProgress
      ? createAggregatedProgress({
          // 50% for face mesh
          // 50% for illumination
          coeffs: [0.5, 0.5],
          onProgress: options.onProgress,
        })
      : undefined

    ;[this.#faceGraph, this.#illuminationGraph, this.#pixelsReader] = await Promise.all([
      createFaceMeshGraph({
        performance: perf,
        onProgress: onProgress?.bind(null, 0),
        runningMode: this._options?.mode,
      }),
      createIlluminationGraph({
        performance: perf,
        onProgress: onProgress?.bind(null, 1),
        initialState: {
          // Assume illumination is perfect at the beginning.
          intensity:
            this._options?.initialState?.illumination.intensity ??
            mean(getLightingIntensityThresholds().perfect),
        },
      }),
      new PixelsReader(),
    ])
    ;[this.#face, this.#illumination] = [
      this.#faceGraph?.out(0).data(),
      this.#illuminationGraph?.out(0).data(),
    ]
  }

  protected _getPixels(imageSource: ImageSource) {
    return this.#pixelsReader?.read(imageSource) ?? null
  }
  protected async _getFaceDetections(imageSource: ImageSource) {
    const faceGraph = this.#faceGraph
    const faceIterator = this.#face

    if (!faceIterator || !faceGraph) return null

    faceGraph.in(0).write(imageSource)

    const face = (await faceIterator.next()).value

    if (faceGraph.hasError())
      throw new AggregateError(faceGraph.getComposedErrors(), "Face processing failed")

    return face
  }
  protected async _getIllumination(face: FaceDetection, pixels: FramePixels | null) {
    const illuminationGraph = this.#illuminationGraph
    const illuminationIterator = this.#illumination

    if (!illuminationIterator || !illuminationGraph || !pixels) return null

    illuminationGraph.in(0).write(pixels)
    illuminationGraph.in(1).write(face)

    const illumination = mapValues((await illuminationIterator.next()).value, (value) =>
      round(value, 2),
    )

    if (illuminationGraph.hasError())
      throw new AggregateError(
        illuminationGraph.getComposedErrors(),
        "Illumination processing failed",
      )

    return illumination
  }

  destroy(): Promise<unknown[]> {
    const promise = Promise.all([
      this.#pixelsReader?.destroy(),
      this.#faceGraph?.destroy(),
      this.#illuminationGraph?.destroy(),
    ])
      .then((result) => {
        return result
      })
      .catch((error) => {
        throw error
      })

    this.#face = undefined
    this.#illumination = undefined

    this.#pixelsReader = undefined
    this.#faceGraph = undefined
    this.#illuminationGraph = undefined

    return promise
  }
}

declare global {
  interface Navigator {
    // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/connection
    connection?: {
      type: string
      effectiveType: string
      downlink: number
      saveData: boolean
    }
  }
}
